import { useMemo } from 'react';
import { CalendarCheck, Clock, Coffee, MapPin, Briefcase, HelpCircle } from 'lucide-react';
import { Skeleton, Button } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import type { StatusTone } from '@vnetwork/ui/data';
import { useMyRecentWorkdays, type MyRecentWorkday } from '@/api/dashboard-personal';
import { WidgetCard } from './WidgetCard';

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type DayStatus = 'normal' | 'late' | 'leave' | 'holiday' | 'remote' | 'business' | 'future' | 'none';

/** Local YYYY-MM-DD (NOT toISOString — that shifts to UTC and moves the date). */
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Monday of the current week. */
function weekMonday(): Date {
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  const mon = new Date(now);
  mon.setDate(now.getDate() + (day === 0 ? -6 : 1 - day));
  mon.setHours(0, 0, 0, 0);
  return mon;
}

/** Mon–Sat day slots for the current week (matches legacy MyWorkdays range). */
function weekDays(): { key: string; date: Date }[] {
  const mon = weekMonday();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(mon);
    d.setDate(mon.getDate() + i);
    return { key: iso(d), date: d };
  });
}

/** Late if morning check-in after 08:30 (or afternoon shift after 13:00). */
function isLate(w: MyRecentWorkday): boolean {
  if (!w.work_day_in) return false;
  const [h, m] = w.work_day_in.split(':').map(Number);
  const mins = h * 60 + m;
  const morning = w.work_day_option === 0 || w.work_day_option === 2 || w.work_day_option === null;
  return mins > (morning ? 8 * 60 + 30 : 13 * 60);
}

/** Derive a display status from a workday record (mirrors legacy logic). */
function deriveStatus(w: MyRecentWorkday): DayStatus {
  const type = (w.work_day_type || '').toLowerCase();
  if (w.business || type.includes('công tác') || type.includes('business')) return 'business';
  if (type.includes('lễ') || type.includes('tết') || type.includes('holiday')) return 'holiday';
  if (type.includes('remote')) return 'remote';
  if (type.includes('phép') || type.includes('không lương') || type.includes('leave') || w.day_off) return 'leave';
  if (w.work_day_in) return isLate(w) ? 'late' : 'normal';
  return 'none';
}

const STATUS_META: Record<DayStatus, { tone: StatusTone; label: string; Icon: typeof Clock }> = {
  normal: { tone: 'success', label: 'Present', Icon: Clock },
  late: { tone: 'warning', label: 'Late', Icon: Clock },
  leave: { tone: 'warning', label: 'Leave', Icon: Coffee },
  holiday: { tone: 'info', label: 'Holiday', Icon: Coffee },
  remote: { tone: 'info', label: 'Remote', Icon: MapPin },
  business: { tone: 'info', label: 'Business trip', Icon: Briefcase },
  future: { tone: 'muted', label: 'Upcoming', Icon: HelpCircle },
  none: { tone: 'muted', label: 'No record', Icon: HelpCircle },
};

/** Secondary detail line: check-in/out times when present, else the raw type. */
function detailText(w: MyRecentWorkday, status: DayStatus): string {
  if ((status === 'normal' || status === 'late') && w.work_day_in) {
    const hrs = w.work_day_time ? ` · ${w.work_day_time.toFixed(1)}h` : '';
    return w.work_day_out ? `In ${w.work_day_in} · Out ${w.work_day_out}${hrs}` : `In ${w.work_day_in}`;
  }
  return w.work_day_type || '';
}

/**
 * My Workdays ("Ngày công của tôi") — this week's attendance rail, one row per
 * day Mon–Sat with weekday, date and a status marker (present/late/leave/
 * holiday/remote/business) or upcoming/no-record. Every weekday is always
 * shown, even when the API has no record for it. Parity with legacy
 * shared/MyWorkdays. Real data from /workdays/my-recent.
 */
export function MyWorkdaysWidget() {
  const days = useMemo(weekDays, []);
  const { gte, lte } = useMemo(() => ({ gte: days[0].key, lte: days[days.length - 1].key }), [days]);
  const { data = [], isLoading } = useMyRecentWorkdays(gte, lte);
  const todayKey = iso(new Date());

  const byDate = useMemo(() => {
    const m = new Map<string, MyRecentWorkday>();
    for (const w of data) m.set(w.work_day_date?.slice(0, 10), w);
    return m;
  }, [data]);

  return (
    <WidgetCard
      title="My Workdays"
      description="Attendance this week"
      actions={
        <Button variant="ghost" size="sm">
          <CalendarCheck size={14} /> Detail
        </Button>
      }
    >
      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 44 }} />
          ))}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          {days.map(({ key, date }) => {
            const record = byDate.get(key);
            const isToday = key === todayKey;
            const status: DayStatus = record ? deriveStatus(record) : key > todayKey ? 'future' : 'none';
            const meta = STATUS_META[status];
            const detail = record ? detailText(record, status) : '';
            return (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--vn-space-3)',
                  padding: 'var(--vn-space-2) var(--vn-space-3)',
                  borderRadius: 'var(--vn-radius-md)',
                  background: isToday ? 'var(--vn-bg-muted)' : 'transparent',
                  borderLeft: isToday ? '3px solid var(--vn-primary-solid)' : '3px solid transparent',
                  opacity: status === 'future' ? 0.6 : 1,
                }}
              >
                {/* Date column */}
                <div style={{ width: 36, textAlign: 'center', flexShrink: 0 }}>
                  <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                    {WEEKDAY[date.getDay()]}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--vn-font-size-lg)',
                      fontWeight: 'var(--vn-font-weight-semibold)',
                      lineHeight: 1.1,
                      color: isToday ? 'var(--vn-primary-text)' : 'var(--vn-fg-default)',
                    }}
                  >
                    {String(date.getDate()).padStart(2, '0')}
                  </div>
                </div>

                {/* Detail */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <StatusPill tone={meta.tone} icon={<meta.Icon size={12} />}>
                    {meta.label}
                  </StatusPill>
                  {detail ? (
                    <div
                      style={{
                        fontSize: 'var(--vn-font-size-xs)',
                        color: 'var(--vn-fg-muted)',
                        marginTop: 'var(--vn-space-1)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {detail}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </WidgetCard>
  );
}
